/**
 * Defensive response normalizers for the Go backend.
 *
 * Several admin endpoints serialise raw domain structs that don't carry
 * `json:"…"` tags (Station, Session, IDTag, Organization, …). Without tags
 * Go emits the exported field names verbatim, so the wire payload arrives
 * as PascalCase (`ID`, `Name`, `IsActive`, `StartTime`, …) instead of the
 * camelCase the apps consume.
 *
 * Rather than special-casing every page we apply a recursive
 * "PascalCase → camelCase" pass on the response and then normalise each
 * entity into the projection the UI was written against. The transforms
 * below are also a no-op for endpoints that already return clean
 * camelCase (e.g. `deviceJSON`, `omStationJSON`), so they are safe to
 * apply unconditionally.
 */
import type {
  ChargingSession,
  Device,
  Page,
  SessionStatus,
  Station,
} from '@eon/types';
import type {
  AdminUser,
  Group,
  GroupInvitation,
  GroupMember,
  IdTag,
  SystemLog,
  UserIdTagRow,
} from './hooks/admin';

/**
 * Acronyms that the existing TypeScript types preserve in their original
 * upper-cased form when they appear as a trailing word inside a field
 * name. These match the conventions baked into `@eon/types` (see
 * `meanPowerKW`, `totalEnergyKWh`, `powerKW`, `maxKW`).
 */
const PRESERVED_TAIL_ACRONYMS = new Set(['KW', 'KWh']);

/**
 * Convert a single object key from PascalCase / SCREAMING acronyms to
 * camelCase, treating acronym runs as a single word.
 *
 *   Name           → name
 *   ID             → id
 *   IDTag          → idTag
 *   URL            → url
 *   URLPath        → urlPath
 *   StartTime      → startTime
 *   DeviceID       → deviceId
 *   ConnectorID    → connectorId
 *   OCPPVersion    → ocppVersion
 *   MeanPowerKW    → meanPowerKW   (KW preserved as a unit suffix)
 *   TotalEnergyKWh → totalEnergyKWh
 *   MyField123     → myField123
 */
export function camelKey(key: string): string {
  if (!key) return key;
  const first = key.charAt(0);
  // Already camelCase — nothing to do.
  if (first === first.toLowerCase()) return key;
  // Word splitter:
  //   [A-Z]+(?=[A-Z][a-z]) — leading acronym before another word ("ID" in "IDTag")
  //   [A-Z][a-z]+          — Title Case word ("Tag", "Device")
  //   [A-Z]+               — trailing or solo acronym ("ID" in "DeviceID", "KW")
  //   [a-z]+               — lowercase tail (defensive — input is usually PascalCase)
  //   [0-9]+               — digit run
  const words = key.match(/[A-Z]+(?=[A-Z][a-z])|[A-Z][a-z]+|[A-Z]+|[a-z]+|[0-9]+/g);
  if (!words || words.length === 0) return key;
  const last = words.length - 1;
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i === 0) return lower;
      if (i === last && PRESERVED_TAIL_ACRONYMS.has(w)) return w;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * Recursively transform PascalCase keys to camelCase across plain objects
 * and arrays. Leaves `Date`, `Map`, class instances, primitives, and `null`
 * untouched.
 */
export function camelizeKeys<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) {
    return input.map((item) => camelizeKeys(item)) as unknown as T;
  }
  if (!isPlainObject(input)) return input as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    out[camelKey(k)] = camelizeKeys(v);
  }
  return out as T;
}

/* -------------------------------------------------------------------------- */
/*  Page envelope                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Normalize a paginated response. Falls back to safe defaults when the
 * backend omits envelope fields (some endpoints respond with just
 * `{data: [...]}`).
 */
export function normalizePage<T>(raw: unknown, item: (entry: unknown) => T): Page<T> {
  const obj = (isPlainObject(raw) ? raw : {}) as Record<string, unknown>;
  const data = Array.isArray(obj.data) ? obj.data : [];
  const pageSize =
    typeof obj.pageSize === 'number'
      ? obj.pageSize
      : typeof obj.PageSize === 'number'
        ? obj.PageSize
        : data.length;
  const page =
    typeof obj.page === 'number' ? obj.page : typeof obj.Page === 'number' ? obj.Page : 1;
  const totalCount =
    typeof obj.totalCount === 'number'
      ? obj.totalCount
      : typeof obj.TotalCount === 'number'
        ? obj.TotalCount
        : data.length;
  const totalPages =
    typeof obj.totalPages === 'number'
      ? obj.totalPages
      : typeof obj.TotalPages === 'number'
        ? obj.TotalPages
        : Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
  return {
    data: data.map((entry) => item(entry)),
    page,
    pageSize,
    totalCount,
    totalPages,
  };
}

/* -------------------------------------------------------------------------- */
/*  Entity normalizers                                                        */
/* -------------------------------------------------------------------------- */

const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;
const bool = (v: unknown, fallback = false): boolean =>
  typeof v === 'boolean' ? v : fallback;

export function normalizeStation(raw: unknown): Station {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const active = bool(r.active ?? r.isActive, false);
  const devicesRaw = Array.isArray(r.devices) ? r.devices : [];
  const devices = devicesRaw.map((d) => {
    const row = camelizeKeys<Record<string, unknown>>(d);
    return {
      id: String(row.id ?? ''),
      serialNumber: String(row.serialNumber ?? ''),
      displayName: str(row.displayName),
      status: str(row.status),
      registered: typeof row.registered === 'boolean' ? row.registered : undefined,
    };
  });
  return {
    id: String(r.id ?? ''),
    name: (r.name as string | undefined) ?? '',
    description: str(r.description),
    organizationId: str(r.organizationId),
    address: str(r.address),
    latitude: num(r.latitude),
    longitude: num(r.longitude),
    city: str(r.city),
    country: str(r.country),
    isPublic: typeof r.isPublic === 'boolean' ? r.isPublic : undefined,
    isActive: typeof r.isActive === 'boolean' ? r.isActive : undefined,
    active,
    deviceCount: typeof r.deviceCount === 'number' ? r.deviceCount : 0,
    contact: (r.contact as Record<string, unknown> | undefined) ?? null,
    operatingHours: (r.operatingHours as Record<string, unknown> | undefined) ?? null,
    amenities: Array.isArray(r.amenities) ? (r.amenities as string[]) : undefined,
    metadata: (r.metadata as Record<string, unknown> | undefined) ?? null,
    devices: devices.length > 0 ? devices : undefined,
    createdAt: str(r.createdAt),
    updatedAt: str(r.updatedAt),
  };
}

export function normalizeSession(raw: unknown): ChargingSession {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const startedAt = (r.startedAt ?? r.startTime ?? '') as string;
  const stoppedAt = (r.stoppedAt ?? r.stopTime ?? null) as string | null;
  const optionalString = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() !== '' ? v : null;
  return {
    id: String(r.id ?? ''),
    deviceId: String(r.deviceId ?? ''),
    connectorId: typeof r.connectorId === 'number' ? r.connectorId : 0,
    transactionId: num(r.transactionId),
    userId: (r.userId as string | undefined) ?? null,
    idTag: (r.idTag as string | undefined) ?? null,
    status: ((r.status as string) ?? 'started') as SessionStatus,
    startedAt,
    stoppedAt,
    durationSeconds: num(r.durationSeconds),
    totalEnergyKWh: num(r.totalEnergyKWh ?? r.totalEnergy),
    meanPowerKW: num(r.meanPowerKW),
    meterStart: num(r.meterStart),
    meterStop: num(r.meterStop),
    userName: optionalString(r.userName),
    deviceDisplayName: optionalString(r.deviceDisplayName),
    stationName: optionalString(r.stationName),
    organizationName: optionalString(r.organizationName ?? r.groupName),
    groupName: optionalString(r.groupName ?? r.organizationName),
  };
}

/**
 * Normalize an ID tag. The admin backend currently returns the raw
 * `IDTag` aggregate (no JSON tags), which uses fields like `Value`,
 * `IsBlocked`, `ExpiryDate`. We project that onto the UI shape:
 * `tag`, `status` (active|blocked|expired) and (when present) owner info.
 */
export function normalizeIdTag(raw: unknown): IdTag {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const value = String(r.value ?? r.tag ?? '');
  const name = String(r.name ?? value);
  const isPhysical = bool(r.isPhysical, String(r.tagType ?? '').toLowerCase() !== 'digital');
  const maxActiveTransactions = Number(r.maxActiveTransactions ?? 1);
  const isBlocked =
    bool(r.isBlocked, false) || maxActiveTransactions === 0 || String(r.status) === 'blocked';
  const expiresAt = (r.expiresAt ?? r.expiryDate ?? null) as string | null;
  const expired =
    !isBlocked && expiresAt ? Date.parse(expiresAt) < Date.now() : false;
  const statusRaw = String(r.status ?? '');
  const status: IdTag['status'] =
    statusRaw === 'active' || statusRaw === 'blocked' || statusRaw === 'expired'
      ? statusRaw
      : isBlocked
        ? 'blocked'
        : expired
          ? 'expired'
          : 'active';
  const tagType: IdTag['tagType'] = isPhysical ? 'Physical' : 'Digital';
  return {
    id: String(r.id ?? ''),
    tag: value || name,
    name,
    value: value || name,
    status: ['active', 'blocked', 'expired'].includes(status) ? status : isBlocked ? 'blocked' : expired ? 'expired' : 'active',
    tagType,
    isPhysical,
    isBlocked,
    ownerName: (r.ownerName as string | undefined) ?? null,
    ownerEmail: (r.ownerEmail as string | undefined) ?? null,
    userId: (r.userId as string | undefined) ?? null,
    parentIdTag: (r.parentIdTag as string | undefined) ?? null,
    description: (r.description as string | undefined) ?? null,
    maxActiveTransactions,
    expiresAt,
    issuedAt: (r.issuedAt as string | undefined) ?? null,
    createdAt: String(r.createdAt ?? r.issuedAt ?? ''),
    updatedAt: (r.updatedAt as string | undefined) ?? null,
  };
}

export function normalizeAdminUser(raw: unknown): AdminUser {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const orgsRaw = Array.isArray(r.organizations) ? r.organizations : [];
  const organizations = orgsRaw.map((o) => {
    const item = camelizeKeys<Record<string, unknown>>(o);
    return {
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      role: String(item.role ?? 'member'),
    };
  });
  return {
    id: String(r.id ?? ''),
    email: (r.email as string | undefined) ?? undefined,
    phone: (r.phone as string | undefined) ?? null,
    name: String(r.name ?? ''),
    surname: (r.surname as string | undefined) ?? undefined,
    role: String(r.role ?? 'user') as AdminUser['role'],
    appType: String(r.appType ?? 'customer') as AdminUser['appType'],
    active: typeof r.active === 'boolean' ? r.active : true,
    registrationComplete:
      typeof r.registrationComplete === 'boolean' ? r.registrationComplete : undefined,
    createdAt: r.createdAt ? String(r.createdAt) : undefined,
    updatedAt: r.updatedAt ? String(r.updatedAt) : undefined,
    lastLoginAt: r.lastLoginAt ? String(r.lastLoginAt) : null,
    language: (r.language as string | undefined) ?? undefined,
    address:
      r.address && typeof r.address === 'object'
        ? (r.address as Record<string, unknown>)
        : null,
    hasPassword: typeof r.hasPassword === 'boolean' ? r.hasPassword : undefined,
    organizations,
  };
}

export function normalizeUserIdTag(raw: unknown): UserIdTagRow {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const tag = String(r.tag ?? r.value ?? '');
  return {
    id: String(r.id ?? ''),
    tag,
    value: tag,
    name: (r.name as string | undefined) ?? undefined,
    isBlocked: Boolean(r.isBlocked),
    isPhysical: Boolean(r.isPhysical),
    expiresAt: r.expiresAt ? String(r.expiresAt) : null,
    createdAt: String(r.createdAt ?? ''),
  };
}

export function normalizeGroup(raw: unknown): Group {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  return {
    id: String(r.id ?? ''),
    name: (r.name as string | undefined) ?? '',
    description: (r.description as string | undefined) ?? null,
    memberCount: typeof r.memberCount === 'number' ? r.memberCount : 0,
    deviceCount: typeof r.deviceCount === 'number' ? r.deviceCount : 0,
    stationCount: typeof r.stationCount === 'number' ? r.stationCount : 0,
    active: typeof r.active === 'boolean' ? r.active : true,
    createdAt: String(r.createdAt ?? ''),
    updatedAt: r.updatedAt ? String(r.updatedAt) : undefined,
  };
}

export function normalizeGroupMember(raw: unknown): GroupMember {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  return {
    userId: String(r.userId ?? ''),
    role: String(r.role ?? 'member'),
    name: (r.name as string | undefined) ?? undefined,
    email: (r.email as string | undefined) ?? undefined,
    phone: (r.phone as string | undefined) ?? undefined,
    joinedAt: String(r.joinedAt ?? ''),
    allowanceKWh: typeof r.allowanceKWh === 'number' ? r.allowanceKWh : undefined,
  };
}

export function normalizeGroupInvitation(raw: unknown): GroupInvitation {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  return {
    id: String(r.id ?? ''),
    email: (r.email as string | undefined) ?? undefined,
    phone: (r.phone as string | undefined) ?? undefined,
    role: String(r.role ?? 'member'),
    status: String(r.status ?? 'pending'),
    expiresAt: String(r.expiresAt ?? ''),
    createdAt: String(r.createdAt ?? ''),
  };
}

export function normalizeSystemLog(raw: unknown): SystemLog {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const levelRaw = String(r.level ?? 'info').toLowerCase();
  const level: SystemLog['level'] =
    levelRaw === 'debug' || levelRaw === 'warn' || levelRaw === 'error' || levelRaw === 'warning'
      ? levelRaw === 'warning'
        ? 'warn'
        : (levelRaw as SystemLog['level'])
      : 'info';
  return {
    id: r.id != null ? String(r.id) : undefined,
    time: String(r.time ?? r.timestamp ?? ''),
    level,
    source: (r.source as string | undefined) ?? null,
    message: String(r.message ?? ''),
    fields:
      r.fields && typeof r.fields === 'object'
        ? (r.fields as Record<string, unknown>)
        : r.metadata && typeof r.metadata === 'object'
          ? (r.metadata as Record<string, unknown>)
          : null,
  };
}

/**
 * Devices already arrive in camelCase via the backend's `deviceJSON`
 * projection, but we still pass them through `camelizeKeys` for safety
 * (covers any future endpoint that doesn't go through the helper) and
 * default the connectors array so the UI never has to null-check it.
 */
export function normalizeDevice(raw: unknown): Device {
  const r = camelizeKeys<Record<string, unknown>>(raw);
  const connectors = Array.isArray(r.connectors)
    ? (r.connectors as Record<string, unknown>[]).map((c) => ({
        id: typeof c.id === 'number' ? c.id : Number(c.id ?? 0),
        status: String(c.status ?? 'Unknown'),
        type: (c.type as string | undefined) ?? null,
        format: (c.format as string | undefined) ?? null,
        errorCode: (c.errorCode as string | undefined) ?? null,
        info: (c.info as string | undefined) ?? null,
        powerKW: num(c.powerKW),
        currentAmps: num(c.currentAmps),
        voltage: num(c.voltage),
        maxKW: num(c.maxKW ?? c.powerKW),
        updatedAt: (c.updatedAt as string | undefined) ?? null,
      }))
    : [];
  return {
    id: String(r.id ?? ''),
    serialNumber: String(r.serialNumber ?? ''),
    displayName: (r.displayName as string | undefined) ?? null,
    description: (r.description as string | undefined) ?? null,
    vendor: (r.vendor as string | undefined) ?? null,
    model: (r.model as string | undefined) ?? null,
    firmware: (r.firmware as string | undefined) ?? null,
    firmwareVersion: (r.firmwareVersion as string | undefined) ?? null,
    ocppVersion: (r.ocppVersion as string | undefined) ?? null,
    status: ((r.status as string) ?? 'unknown') as Device['status'],
    lastHeartbeat: (r.lastHeartbeat as string | undefined) ?? null,
    lastHeartbeatAt: (r.lastHeartbeatAt as string | undefined) ?? null,
    onlineSince: (r.onlineSince as string | undefined) ?? null,
    organizationId: (r.organizationId as string | undefined) ?? null,
    stationId: (r.stationId as string | undefined) ?? null,
    registered: bool(r.registered, false),
    isPublic: bool(r.isPublic, false),
    isActive: bool(r.isActive, true),
    qrToken: (r.qrToken as string | undefined) ?? null,
    metadata:
      r.metadata && typeof r.metadata === 'object'
        ? (r.metadata as Record<string, unknown>)
        : null,
    connectors,
    createdAt: (r.createdAt as string | undefined) ?? undefined,
    updatedAt: (r.updatedAt as string | undefined) ?? undefined,
  };
}
